import React from 'react';
import { CheckCircle2, AlertCircle, ExternalLink, Loader2, ShieldCheck } from 'lucide-react';

/**
 * Sub-components for StripeConnectCard. Pure-presentational; the parent owns
 * all state and side effects.
 */

export function StripeConnectHeader({ onboarded }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${onboarded ? 'bg-emerald-500/15' : 'bg-[#635bff]/15'}`}>
          {onboarded ? <ShieldCheck className="h-5 w-5 text-emerald-400" /> : <span className="text-[#635bff] font-bold">S</span>}
        </div>
        <div>
          <h3 className="text-sm font-bold tracking-tight">Stripe Connect — Direct Payouts &amp; KYC</h3>
          <p className="text-[11px] text-gray-500 mt-0.5 max-w-md leading-relaxed">
            Verify your business once with Stripe&apos;s hosted KYC. Lets you collect payments,
            receive direct payouts, and stay compliant — all without leaving WAVE.
          </p>
        </div>
      </div>
      {onboarded && (
        <span className="text-[10px] uppercase tracking-widest font-bold bg-emerald-500/15 text-emerald-300 px-2 py-1 rounded-full" data-testid="stripe-connect-status-pill">
          ACTIVE
        </span>
      )}
    </div>
  );
}

export function StatusPill({ label, ok }) {
  return (
    <div className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border ${ok ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-300' : 'border-[#1f1f1f] bg-[#0A0A0A] text-gray-500'}`}>
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="h-3.5 w-3.5 rounded-full border border-current" />}
      <span className="font-semibold">{label}</span>
    </div>
  );
}

export function StatusGrid({ status }) {
  return (
    <div className="mt-5 grid grid-cols-3 gap-3 text-[11px]">
      <StatusPill label="Account created" ok={status.has_account} />
      <StatusPill label="Details submitted" ok={status.details_submitted} />
      <StatusPill label="Charges enabled" ok={status.charges_enabled} />
    </div>
  );
}

export function RequirementsList({ items }) {
  if (!items?.length) return null;
  return (
    <div className="mt-4 text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3" data-testid="stripe-requirements">
      <div className="flex items-center gap-2 mb-1.5">
        <AlertCircle className="h-3.5 w-3.5" /><span className="font-bold">Stripe needs more info</span>
      </div>
      <ul className="list-disc ml-4 space-y-0.5 opacity-80">
        {items.slice(0, 4).map(r => <li key={r}>{r.replace(/_/g, ' ')}</li>)}
      </ul>
    </div>
  );
}

export function ConnectActionButtons({ status, busy, onStart, onDashboard, onRefresh }) {
  const onboarded = status.onboarding_complete;
  const PrimaryButton = onboarded ? (
    <button
      onClick={onDashboard}
      disabled={busy}
      data-testid="stripe-connect-dashboard-btn"
      className="px-4 py-2.5 rounded-lg border border-[#635bff]/40 hover:bg-[#635bff]/10 text-[#a5b4fc] text-xs font-bold transition flex items-center gap-2"
    >
      Open Stripe dashboard <ExternalLink className="h-3.5 w-3.5" />
    </button>
  ) : (
    <button
      onClick={onStart}
      disabled={busy}
      data-testid={status.has_account ? 'stripe-connect-resume-btn' : 'stripe-connect-start-btn'}
      className="px-4 py-2.5 rounded-lg bg-[#635bff] hover:brightness-110 text-white text-xs font-bold transition disabled:opacity-50 flex items-center gap-2"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
      {status.has_account ? 'Continue onboarding' : 'Connect Stripe'}
      <ExternalLink className="h-3.5 w-3.5" />
    </button>
  );

  return (
    <div className="mt-5 flex flex-wrap gap-2">
      {PrimaryButton}
      <button
        onClick={onRefresh}
        disabled={busy}
        data-testid="stripe-connect-refresh-btn"
        className="px-3 py-2.5 rounded-lg border border-[#1f1f1f] hover:border-[#00D4FF] text-[10px] uppercase tracking-widest font-bold text-gray-400 transition"
      >
        Refresh
      </button>
    </div>
  );
}
